import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RateProvider } from "@/context/RateContext";
import { I18nProvider } from "@/context/I18nContext";
import { SubscriptionProvider, useSubscription } from "@/context/SubscriptionContext";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import POS from "@/pages/POS";
import Vision from "@/pages/Vision";
import Spectrometer from "@/pages/Spectrometer";
import Notifications from "@/pages/Notifications";
import ZReport from "@/pages/ZReport";
import Settings from "@/pages/Settings";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import VaultPage from "@/pages/Vault";
import Accounts from "@/pages/Accounts";
import Analytics from "@/pages/Analytics";
import CashCount from "@/pages/CashCount";
import Karats from "@/pages/Karats";
import Chat from "@/pages/Chat";
import StockAlerts from "@/pages/StockAlerts";
import Signage from "@/pages/Signage";
import Users from "@/pages/Users";
import Pricing from "@/pages/Pricing";
import { BillingSuccess, BillingCancel } from "@/pages/BillingResult";
import Subscription from "@/pages/Subscription";
import SuperAdmin from "@/pages/SuperAdmin";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import SupportWidget from "@/components/SupportWidget";
import LoadingScreen from "@/components/LoadingScreen";
import RouteTitle from "@/components/RouteTitle";
import UpdatePrompt from "@/components/UpdatePrompt";
import MesafeliSatis from "@/pages/legal/MesafeliSatis";
import IptalIade from "@/pages/legal/IptalIade";
import Kvkk from "@/pages/legal/Kvkk";
import HizmetTeslimati from "@/pages/legal/HizmetTeslimati";
import KullanimKosullari from "@/pages/legal/KullanimKosullari";
import Iletisim from "@/pages/legal/Iletisim";

const SUPERADMIN_EMAILS = new Set(["mstfayldz357@gmail.com"]);

function Guard({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-neutral-400">Yükleniyor…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Owner-only guard. Any user other than the platform owner is bounced to
// /dashboard so URL-typing cannot leak access to the SuperAdmin panel.
function SuperAdminGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-neutral-400">Yükleniyor…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!SUPERADMIN_EMAILS.has((user.email || "").toLowerCase())) return <Navigate to="/dashboard" replace />;
  return children;
}

// Hard-lock: if tenant has no active subscription AND no live trial, we redirect
// them to /pricing (their only escape valve besides /subscription & /logout).
function SubGate({ children }) {
  const { hasActive, loading } = useSubscription();
  if (loading) return <div className="p-10 text-neutral-400">Yükleniyor…</div>;
  if (!hasActive) return <Navigate to="/pricing" replace />;
  return children;
}

function Shell() {
  return (
    <RateProvider>
      <Layout>
        <Routes>
          <Route path="/dashboard" element={<Guard><SubGate><Dashboard /></SubGate></Guard>} />
          <Route path="/products" element={<Guard><SubGate><Products /></SubGate></Guard>} />
          <Route path="/stock-alerts" element={<Guard roles={["admin","kasiyer"]}><SubGate><StockAlerts /></SubGate></Guard>} />
          <Route path="/pos" element={<Guard><SubGate><POS /></SubGate></Guard>} />
          <Route path="/karats" element={<Guard><SubGate><Karats /></SubGate></Guard>} />
          <Route path="/customers" element={<Guard roles={["admin","kasiyer"]}><SubGate><Customers /></SubGate></Guard>} />
          <Route path="/customers/:id" element={<Guard roles={["admin","kasiyer"]}><SubGate><CustomerDetail /></SubGate></Guard>} />
          <Route path="/vault" element={<Guard roles={["admin","kasiyer"]}><SubGate><VaultPage /></SubGate></Guard>} />
          <Route path="/accounts" element={<Guard roles={["admin","kasiyer"]}><SubGate><Accounts /></SubGate></Guard>} />
          <Route path="/analytics" element={<Guard roles={["admin","kasiyer"]}><SubGate><Analytics /></SubGate></Guard>} />
          <Route path="/cashcount" element={<Guard roles={["admin","kasiyer"]}><SubGate><CashCount /></SubGate></Guard>} />
          <Route path="/vision" element={<Guard><SubGate><Vision /></SubGate></Guard>} />
          <Route path="/chat" element={<Guard><SubGate><Chat /></SubGate></Guard>} />
          <Route path="/spectro" element={<Guard><SubGate><Spectrometer /></SubGate></Guard>} />
          <Route path="/notify" element={<Guard roles={["admin","kasiyer"]}><SubGate><Notifications /></SubGate></Guard>} />
          <Route path="/z-report" element={<Guard roles={["admin","kasiyer"]}><SubGate><ZReport /></SubGate></Guard>} />
          <Route path="/settings" element={<Guard roles={["admin"]}><SubGate><Settings /></SubGate></Guard>} />
          <Route path="/users" element={<Guard roles={["admin"]}><SubGate><Users /></SubGate></Guard>} />
          <Route path="/subscription" element={<Guard><Subscription /></Guard>} />
          <Route path="/superadmin" element={<SuperAdminGuard><SuperAdmin /></SuperAdminGuard>} />
        </Routes>
      </Layout>
    </RateProvider>
  );
}

export default function App() {
  const [booting, setBooting] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setBooting(false), 2400);
    return () => clearTimeout(t);
  }, []);
  return (
    <BrowserRouter>
      <LoadingScreen visible={booting} />
      <RouteTitle />
      <I18nProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Public legal / compliance pages (Iyzico prerequisites) */}
              <Route path="/legal/mesafeli-satis" element={<MesafeliSatis />} />
              <Route path="/legal/iptal-iade" element={<IptalIade />} />
              <Route path="/legal/gizlilik-kvkk" element={<Kvkk />} />
              <Route path="/legal/hizmet-teslimati" element={<HizmetTeslimati />} />
              <Route path="/legal/kullanim-kosullari" element={<KullanimKosullari />} />
              <Route path="/iletisim" element={<Iletisim />} />

              <Route path="/pricing" element={<Guard><Pricing /></Guard>} />
              <Route path="/billing/success" element={<Guard><BillingSuccess /></Guard>} />
              <Route path="/billing/cancel" element={<Guard><BillingCancel /></Guard>} />
              <Route path="/signage" element={<Guard><Signage /></Guard>} />
              <Route path="/*" element={<Shell />} />
            </Routes>
            <PWAInstallPrompt />
            <SupportWidget />
            <UpdatePrompt />
          </SubscriptionProvider>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}
